import {
  countries as countriesList,
  CountryItem,
  RegionItem,
} from "@shared/lib/countries";
import { useEffect, useMemo, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "@shared/utils/internationalization";
import { SearchAddressFormProps } from "./SearchAddressForm.props";

// Create lookup maps for faster access
// Create them in this file instead of where the countries[] come from due to
// the specificity of using names as keys instead of ids.
const countriesMap: Map<string, CountryItem> = new Map();

const regionsMap: Map<string, Map<string, RegionItem>> = new Map();

// Populate the maps
countriesList.forEach((country) => {
  // Add country to countries map
  countriesMap.set(country.name, country);

  // Create regions map for this country
  const countryRegionsMap: Map<string, RegionItem> = new Map();

  // Add each region to the regions map
  if (country.regions) {
    country.regions.forEach((region) => {
      countryRegionsMap.set(region.name, region);
    });
  }

  // Add the country's regions map to the main regions map
  regionsMap.set(country.name, countryRegionsMap);
});

// react-hook-form is supported by React Native, so form logic can be used in this shared hook
export function useSearchAddressForm(
  form: UseFormReturn,
  formId: string,
  updateAddressCallback?: SearchAddressFormProps["updateAddressCallback"],
  allowedCountries?: SearchAddressFormProps["allowedCountries"]
) {
  const streetNameId = `${formId}_info_home_address_street_name`;
  const unitNumberId = `${formId}_info_home_address_unit_number`;
  const countryId = `${formId}_info_home_address_country`;
  const regionId = `${formId}_info_home_address_province`;
  const cityId = `${formId}_info_home_address_city`;
  const postalCodeId = `${formId}_info_home_address_postal`;

  const { t, i18n } = useTranslation("common");

  const validateAllowedCountry = (value: string) => {
    if (!allowedCountries || allowedCountries.length === 0) {
      return true; // No restrictions, all countries allowed
    }

    if (!value) {
      return true; // Empty value is handled by required validation
    }

    return (
      allowedCountries.includes(value) ||
      t(
        "Addresses to {{value}} are not allowed. Selection is limited to the following countries {{countries}}.",
        {
          value,
          countries: new Intl.ListFormat(i18n.language, {
            style: "long",
            type: "conjunction",
          }).format(allowedCountries),
        }
      )
    );
  };

  // takes initial country from form value, and returns the country object
  const getInitialCountry = () => {
    const countryName = form.getValues(countryId);
    return countriesMap.get(countryName);
  };
  const [selectedCountryItem, setSelectedCountryItem] = useState<
    CountryItem | undefined
  >(() => getInitialCountry());

  // takes initial region from form value, and returns the region object
  const getInitialRegion = () => {
    if (!selectedCountryItem) return undefined;

    const regionName = form.getValues(regionId);
    const countryRegionsMap = regionsMap.get(selectedCountryItem.name);

    if (!countryRegionsMap) return undefined;

    // Return the region if it exists, otherwise return the first region
    return (
      countryRegionsMap.get(regionName) ||
      (selectedCountryItem.regions && selectedCountryItem.regions.length > 0
        ? selectedCountryItem.regions[0]
        : undefined)
    );
  };
  const [selectedRegionItem, setSelectedRegionItem] = useState<
    RegionItem | undefined
  >(() => getInitialRegion());

  // Watch for changes to country field
  const watchedCountry = useWatch({
    control: form.control,
    name: countryId,
  });

  // Watch for changes to region field
  const watchedRegion = useWatch({
    control: form.control,
    name: regionId,
  });

  // Update country state when form country value changes
  useEffect(() => {
    if (!watchedCountry) return;

    // Only update if the country has actually changed
    if (selectedCountryItem?.name !== watchedCountry) {
      const newCountry = countriesMap.get(watchedCountry);

      if (newCountry) {
        setSelectedCountryItem(newCountry);

        // When country changes, we may need to update region as well
        if (newCountry.regions && newCountry.regions.length > 0) {
          const currentRegionName = form.getValues(regionId);
          const countryRegionsMap = regionsMap.get(newCountry.name);
          const regionExists = countryRegionsMap?.has(currentRegionName);

          if (!regionExists) {
            // Set to first available region if current region doesn't exist in new country
            setSelectedRegionItem(newCountry.regions[0]);
            form.setValue(regionId, newCountry.regions[0].name, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }
      }
    }
  }, [watchedCountry, regionId, form]);

  // Update region state when form region value changes
  useEffect(() => {
    if (!watchedRegion || !selectedCountryItem?.regions) return;

    // Only update if the region has actually changed
    if (selectedRegionItem?.name !== watchedRegion) {
      const countryRegionsMap = regionsMap.get(selectedCountryItem.name);
      const newRegion = countryRegionsMap?.get(watchedRegion);

      if (newRegion) {
        setSelectedRegionItem(newRegion);
      }
    }
  }, [watchedRegion, selectedCountryItem?.regions, selectedRegionItem?.name]);

  const updateInput = (inputId: string, value: string | undefined) => {
    form.setValue(inputId, value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // trigger form validation for the updated input
    form.trigger(inputId);

    if (!updateAddressCallback) return;

    // update the address object
    const addressObj = form.getValues();

    updateAddressCallback({
      info_home_address_street_name: addressObj[streetNameId],
      info_home_address_unit_number: addressObj[unitNumberId],
      info_home_address_country: addressObj[countryId],
      info_home_address_province: addressObj[regionId],
      info_home_address_city: addressObj[cityId],
      info_home_address_postal: addressObj[postalCodeId],
    });
  };

  const updateCountry = (selectedItem: undefined | null | CountryItem) => {
    if (selectedItem && selectedItem.regions) {
      const firstAvailableRegion = selectedItem.regions[0];

      setSelectedCountryItem(selectedItem);
      setSelectedRegionItem(firstAvailableRegion ?? null);

      // validate and update the form
      form.setValue(countryId, selectedItem.name, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue(regionId, firstAvailableRegion?.name ?? "", {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.trigger(countryId);
      form.trigger(regionId);

      if (updateAddressCallback) {
        const addressObj = form.getValues();
        updateAddressCallback({
          info_home_address_street_name: addressObj[streetNameId],
          info_home_address_unit_number: addressObj[unitNumberId],
          info_home_address_country: selectedItem.name,
          info_home_address_province: firstAvailableRegion?.name ?? "",
          info_home_address_city: addressObj[cityId],
          info_home_address_postal: addressObj[postalCodeId],
        });
      }
    }
  };

  const updateRegion = (selectedItem: undefined | null | RegionItem) => {
    if (selectedItem) {
      setSelectedRegionItem(selectedItem);

      form.setValue(regionId, selectedItem.name, {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.trigger(regionId);

      if (updateAddressCallback) {
        const addressObj = form.getValues();
        updateAddressCallback({
          info_home_address_street_name: addressObj[streetNameId],
          info_home_address_unit_number: addressObj[unitNumberId],
          info_home_address_country: addressObj[countryId],
          info_home_address_province: selectedItem.name,
          info_home_address_city: addressObj[cityId],
          info_home_address_postal: addressObj[postalCodeId],
        });
      }
    }
  };

  const initializeForm = (initialValuesData: Record<string, string>) => {
    Object.keys(initialValuesData).forEach((key) => {
      form.setValue(key, initialValuesData[key]);
      if (key.includes("info_home_address_country")) {
        updateCountry(countriesMap.get(initialValuesData[key]));
      } else if (key.includes("info_home_address_province")) {
        updateRegion(
          regionsMap
            .get(selectedCountryItem?.name || "")
            ?.get(initialValuesData[key])
        );
      }
    });
  };

  const permittedCountries = useMemo(() => {
    if (!allowedCountries || allowedCountries.length === 0) {
      return countriesList;
    }

    const allowedCountriesList = allowedCountries
      .map((countryName) => countriesMap.get(countryName))
      .filter((country) => Boolean(country)) as CountryItem[]; // assertion since typescript doesn't know that the filter will return a CountryItem[]

    // Shouldn't be possible to not allow any countries, so return all countries in that case
    return allowedCountriesList.length > 0
      ? allowedCountriesList
      : countriesList;
  }, [allowedCountries]);

  const getCountryItem = (countryName: string) => {
    return countriesMap.get(countryName);
  };

  return {
    streetNameId,
    unitNumberId,
    countryId,
    regionId,
    cityId,
    postalCodeId,
    selectedCountryItem,
    selectedRegionItem,
    permittedCountries,
    updateCountry,
    updateRegion,
    updateInput,
    initializeForm,
    validateAllowedCountry,
    getCountryItem,
  };
}
